import { PlusOutlined, SettingOutlined, ToolOutlined } from '@ant-design/icons';
import { ServiceOrderExtraForm } from '@components';
import { useTenantVat, useView } from '@hooks';
import { L } from '@lib';
import { InitialServiceOrderExtra, ServiceOrder, ServiceOrderExtra } from '@services';
import { useStore } from '@stores';
import { Utils } from '@utils';
import { Badge, Button, Form, Modal, Tag, Typography } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback, useState } from 'react';

const { Text } = Typography;

interface IProps {
  serviceOrders: ServiceOrder[];
  serviceOrderExtras: ServiceOrderExtra[];
  handleServiceOrdersUpdate?: (serviceOrder: ServiceOrder) => void;
}

const sectionStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #f0f7ff 0%, #f8fbff 100%)',
  borderRadius: 10,
  border: '1px solid #d4e4f7',
  padding: '16px 16px 12px',
  marginBottom: 8,
};

const iconWrapperStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: 6,
  background: '#2CBDFF',
  color: '#fff',
  fontSize: 14,
};

const headerLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#8c8c8c',
  textTransform: 'uppercase',
  letterSpacing: 0.3,
};

export const InvoiceServiceList: React.FC<IProps> = observer(({
  serviceOrders,
  serviceOrderExtras,
  handleServiceOrdersUpdate,
}) => {
  const { isMobile } = useView();
  const vat = useTenantVat();
  const { orderStore, serviceStore } = useStore();
  const { selectServices } = serviceStore;
  const [addForm] = Form.useForm();
  const [showAddModal, setShowAddModal] = useState(false);

  const handleAdd = () => {
    addForm.setFieldsValue({
      ...InitialServiceOrderExtra,
      serviceOrderId: serviceOrders.length === 1 ? serviceOrders[0].id : undefined,
    });
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    addForm.resetFields();
  };

  const handleCreate = useCallback(
    async (values: any) => {
      const resetFormAndCloseModal = () => {
        setShowAddModal(false);
        addForm.resetFields();
      };

      const updateServiceOrder = (updatedOrder: any) => {
        if (updatedOrder && handleServiceOrdersUpdate) {
          handleServiceOrdersUpdate(updatedOrder);
        }
      };

      const isService = addForm.getFieldValue('type') === 'Service';

      if (isService) {
        const selectedService = selectServices.get(values.serviceId);
        const vatValue = selectedService?.isVat ? vat : 0;

        const result = await orderStore.createServiceOrder({
          ...values,
          vat: vatValue,
          extras: [],
          orderId: serviceOrders[0]?.orderId,
        });

        resetFormAndCloseModal();
        if (!result) return;

        const updatedOrder = serviceOrders.find((order) => order.id === result.id);
        updateServiceOrder(updatedOrder);
      } else {
        const result = await orderStore.createExtra({
          ...values,
          vat,
        });

        resetFormAndCloseModal();
        if (!result) return;

        const updatedOrder = serviceOrders.find((order) => order.id === result.serviceOrderId);
        if (updatedOrder) {
          updatedOrder.extras.push(result);
          updateServiceOrder(updatedOrder);
        }
      }
    },
    [orderStore, addForm, vat, serviceOrders, selectServices, handleServiceOrdersUpdate]
  );

  const getExtrasForService = (serviceOrderId: string) =>
    serviceOrderExtras.filter((e) => e.serviceOrderId === serviceOrderId);

  const calculateServiceTotal = (so: ServiceOrder) => {
    const priceAmount = so.isFixed ? so.price : so.price * so.duration;
    const discountAmount = priceAmount * (so.discount / 100);
    const subTotal = Utils.roundToTwoDecimals(priceAmount - discountAmount);
    const vatAmount = so.vat > 0 ? Utils.roundToTwoDecimals((subTotal * vat) / 100) : 0;

    const extras = getExtrasForService(so.id);
    const partsTotal = extras.reduce((acc, e) => {
      const partPrice = e.price * e.quantity;
      const partDiscount = partPrice * (e.discount / 100);
      const partSub = partPrice - partDiscount;
      const partVat = e.vat > 0 ? Utils.roundToTwoDecimals((partSub * vat) / 100) : 0;
      return acc + partSub + partVat;
    }, 0);

    return Utils.roundToTwoDecimals(subTotal + vatAmount + partsTotal);
  };

  const grandTotal = serviceOrders.reduce((acc, so) => acc + calculateServiceTotal(so), 0);

  const grandLabour = Utils.roundToTwoDecimals(
    serviceOrders.reduce((acc, so) => {
      const priceAmount = so.isFixed ? so.price : so.price * so.duration;
      const discountAmount = priceAmount * (so.discount / 100);
      return acc + (priceAmount - discountAmount);
    }, 0)
  );

  const grandParts = Utils.roundToTwoDecimals(
    serviceOrderExtras.reduce((acc, e) => {
      const partPrice = e.price * e.quantity;
      const partDiscount = partPrice * (e.discount / 100);
      return acc + (partPrice - partDiscount);
    }, 0)
  );

  const grandVat = Utils.roundToTwoDecimals(
    serviceOrders.reduce((acc, so) => {
      const priceAmount = so.isFixed ? so.price : so.price * so.duration;
      const discountAmount = priceAmount * (so.discount / 100);
      const subTotal = priceAmount - discountAmount;
      return acc + (so.vat > 0 ? (subTotal * vat) / 100 : 0);
    }, 0) +
      serviceOrderExtras.reduce((acc, e) => {
        const partPrice = e.price * e.quantity;
        const partDiscount = partPrice * (e.discount / 100);
        const partSub = partPrice - partDiscount;
        return acc + (e.vat > 0 ? (partSub * vat) / 100 : 0);
      }, 0)
  );

  // Grid matching OrderServiceSelect: Name | Price | Qty/Dur | Disc | Sub | VAT | Total
  const gridCols = '1fr 80px 70px 70px 90px 90px 100px';

  const addExtraButton = handleServiceOrdersUpdate ? (
    <Button
      size="large"
      onClick={(e) => {
        e.currentTarget.blur();
        handleAdd();
      }}
      icon={<PlusOutlined />}
      style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: '#fff' }}
    >
      {L('Add extra')}
    </Button>
  ) : null;

  const addExtraModal = handleServiceOrdersUpdate ? (
    <Modal
      onCancel={handleCloseAddModal}
      footer={null}
      open={showAddModal}
      width={560}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PlusOutlined style={{ fontSize: 16, color: '#52c41a' }} />
          </div>
          <span style={{ fontSize: 16, fontWeight: 600 }}>{L('Add extra')}</span>
        </div>
      }
      styles={{
        header: { borderBottom: '1px solid #f0f0f0', paddingBottom: 12 },
        body: { paddingTop: 16 },
      }}
    >
      <ServiceOrderExtraForm
        form={addForm}
        serviceOrders={serviceOrders}
        onFinish={handleCreate}
        onCancel={handleCloseAddModal}
      />
    </Modal>
  ) : null;

  const renderServiceCard = (so: ServiceOrder) => {
    const priceAmount = so.isFixed ? so.price : so.price * so.duration;
    const discountAmount = priceAmount * (so.discount / 100);
    const subTotal = Utils.roundToTwoDecimals(priceAmount - discountAmount);
    const vatAmount = so.vat > 0 ? Utils.roundToTwoDecimals((subTotal * vat) / 100) : 0;
    const extras = getExtrasForService(so.id);

    return (
      <div
        key={so.id}
        style={{
          background: '#fff',
          border: '1px solid #d4e4f7',
          borderRadius: 8,
          padding: '8px 0',
          marginBottom: 8,
        }}
      >
        {/* Service row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: gridCols,
            gap: 8,
            alignItems: 'end',
            padding: '6px 10px',
            marginBottom: 4,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 4 }}>
            <ToolOutlined style={{ fontSize: 13, color: '#2CBDFF' }} />
            <Text style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a', whiteSpace: 'nowrap' }}>
              {so.title}
            </Text>
            <Tag
              color={so.isFixed ? 'green' : 'blue'}
              style={{ margin: 0, fontSize: 12, padding: '2px 8px', borderRadius: 4, flexShrink: 0 }}
            >
              {so.isFixed ? 'Fixed' : 'Labour'}
            </Tag>
            {so.note && (
              <Text style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', fontStyle: 'italic' }}>
                {so.note}
              </Text>
            )}
          </div>
          <div style={{ paddingBottom: 4, textAlign: 'right' }}>
            <Text style={headerLabelStyle}>Price (£)</Text>
            <span style={{ fontSize: 13, fontWeight: 500, color: '#555', display: 'block' }}>
              {Utils.formatCurrency(so.price)}
            </span>
          </div>
          <div style={{ paddingBottom: 4, textAlign: 'right' }}>
            <Text style={headerLabelStyle}>Hrs / Qty</Text>
            <span style={{ fontSize: 13, fontWeight: 500, color: '#555', display: 'block' }}>
              {so.isFixed ? '—' : `${so.duration}h`}
            </span>
          </div>
          <div style={{ paddingBottom: 4, textAlign: 'right' }}>
            <Text style={headerLabelStyle}>Disc. %</Text>
            <span style={{ fontSize: 13, fontWeight: 500, color: '#555', display: 'block' }}>
              {so.discount > 0 ? `${so.discount}%` : '—'}
            </span>
          </div>
          <div style={{ paddingBottom: 4, textAlign: 'right' }}>
            <Text style={headerLabelStyle}>Sub Total</Text>
            <span style={{ fontSize: 13, fontWeight: 500, color: '#555', display: 'block' }}>
              {Utils.formatCurrency(subTotal)}
            </span>
          </div>
          <div style={{ paddingBottom: 4, textAlign: 'right' }}>
            <Text style={headerLabelStyle}>VAT</Text>
            <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.45)', display: 'block' }}>
              {Utils.formatCurrency(vatAmount)}
            </span>
          </div>
          <div style={{ paddingBottom: 4, textAlign: 'right' }}>
            <Text style={headerLabelStyle}>Total</Text>
            <Tag
              color="blue"
              style={{
                fontSize: 13,
                fontWeight: 700,
                padding: '2px 8px',
                margin: 0,
                minWidth: 70,
                textAlign: 'center',
                display: 'inline-block',
              }}
            >
              {Utils.formatCurrency(subTotal + vatAmount)}
            </Tag>
          </div>
        </div>

        {/* Extras/Parts */}
        {extras.length > 0 && (
          <div style={{ borderTop: '1px solid #e8eef5', margin: '0 10px' }} />
        )}
        {extras.map((extra, idx) => {
          const partPrice = extra.price * extra.quantity;
          const partDiscount = partPrice * (extra.discount / 100);
          const partSub = Utils.roundToTwoDecimals(partPrice - partDiscount);
          const partVat = extra.vat > 0 ? Utils.roundToTwoDecimals((partSub * vat) / 100) : 0;
          return (
            <div
              key={extra.id}
              style={{
                display: 'grid',
                gridTemplateColumns: gridCols,
                gap: 8,
                alignItems: 'center',
                padding: '6px 10px',
                background: idx % 2 === 0 ? '#fafcff' : '#fff',
                borderBottom: idx < extras.length - 1 ? '1px solid #f0f4f8' : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <SettingOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />
                <Text style={{ fontSize: 13, color: '#555' }}>{extra.name}</Text>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 13, color: '#555' }}>
                  {Utils.formatCurrency(extra.price)}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 13, color: '#555' }}>
                  {extra.quantity}{extra.type === 'Fluid' ? 'L' : ''}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 13, color: '#555' }}>
                  {extra.discount > 0 ? `${extra.discount}%` : '—'}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 13, color: '#555' }}>
                  {Utils.formatCurrency(partSub)}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.45)' }}>
                  {Utils.formatCurrency(partVat)}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <Tag
                  color="blue"
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    padding: '2px 8px',
                    margin: 0,
                    minWidth: 70,
                    textAlign: 'center',
                    display: 'inline-block',
                  }}
                >
                  {Utils.formatCurrency(partSub + partVat)}
                </Tag>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderMobileServiceCard = (so: ServiceOrder) => {
    const priceAmount = so.isFixed ? so.price : so.price * so.duration;
    const discountAmount = priceAmount * (so.discount / 100);
    const subTotal = Utils.roundToTwoDecimals(priceAmount - discountAmount);
    const vatAmount = so.vat > 0 ? Utils.roundToTwoDecimals((subTotal * vat) / 100) : 0;
    const extras = getExtrasForService(so.id);
    const total = calculateServiceTotal(so);

    const infoRow = (label: string, value: string) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {label}
        </Text>
        <Text style={{ fontSize: 13, fontWeight: 500 }}>{value}</Text>
      </div>
    );

    return (
      <div
        key={so.id}
        style={{
          background: '#fff',
          border: '1px solid #d4e4f7',
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 8,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}
        >
          <Text strong style={{ fontSize: 14 }}>
            {so.title}
          </Text>
          <Tag
            color="blue"
            style={{ margin: 0, fontSize: 13, fontWeight: 700, padding: '2px 8px' }}
          >
            {Utils.formatCurrency(total)}
          </Tag>
        </div>
        <Tag color={so.isFixed ? 'green' : 'blue'} style={{ margin: '0 0 8px', fontSize: 11 }}>
          {so.isFixed ? 'Fixed' : 'Labour'}
        </Tag>
        {infoRow('Price', Utils.formatCurrency(so.price))}
        {!so.isFixed && infoRow('Hours', String(so.duration))}
        {so.discount > 0 && infoRow('Discount', `${so.discount}%`)}
        {infoRow('Subtotal', Utils.formatCurrency(subTotal))}
        {infoRow('VAT', Utils.formatCurrency(vatAmount))}
        {so.note && (
          <Text
            type="secondary"
            style={{ fontSize: 12, fontStyle: 'italic', display: 'block', marginTop: 4 }}
          >
            Note: {so.note}
          </Text>
        )}
        {extras.length > 0 && (
          <div style={{ marginTop: 8, borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
            <Text
              strong
              style={{
                fontSize: 12,
                color: '#8c8c8c',
                textTransform: 'uppercase',
                letterSpacing: 0.3,
              }}
            >
              Parts & Extras
            </Text>
            {extras.map((extra) => {
              const partPrice = extra.price * extra.quantity;
              const partDiscount = partPrice * (extra.discount / 100);
              const partSub = Utils.roundToTwoDecimals(partPrice - partDiscount);
              const partVat =
                extra.vat > 0 ? Utils.roundToTwoDecimals((partSub * vat) / 100) : 0;
              return (
                <div
                  key={extra.id}
                  style={{
                    marginTop: 6,
                    paddingLeft: 8,
                    borderLeft: '2px solid #d4e4f7',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{ fontSize: 13 }}>{extra.name}</Text>
                    <Text strong style={{ fontSize: 13 }}>
                      {Utils.formatCurrency(partSub + partVat)}
                    </Text>
                  </div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {Utils.formatCurrency(extra.price)} x {extra.quantity}{extra.type === 'Fluid' ? 'L' : ''}
                    {extra.discount > 0 ? ` (-${extra.discount}%)` : ''}
                  </Text>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (isMobile) {
    return (
      <div style={{ ...sectionStyle, padding: '12px 8px 8px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 10,
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                ...iconWrapperStyle,
                width: 24,
                height: 24,
                borderRadius: 5,
                fontSize: 12,
              }}
            >
              <ToolOutlined />
            </div>
            <Text style={{ fontSize: 14, fontWeight: 700, color: '#1a3353' }}>Services</Text>
            <Badge
              count={serviceOrders.length}
              style={{ backgroundColor: '#2CBDFF', fontSize: 10, fontWeight: 600 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: '#555' }}>
              L: <strong>{Utils.formatCurrency(grandLabour)}</strong>
            </span>
            <span style={{ fontSize: 11, color: '#555' }}>
              P: <strong>{Utils.formatCurrency(grandParts)}</strong>
            </span>
            <span style={{ fontSize: 11, color: '#555' }}>
              VAT: <strong>{Utils.formatCurrency(grandVat)}</strong>
            </span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: '#1a3353',
                background: '#e8f4fd',
                padding: '2px 10px',
                borderRadius: 4,
              }}
            >
              {Utils.formatCurrency(grandTotal)}
            </span>
          </div>
        </div>
        {serviceOrders.map(renderMobileServiceCard)}
        {addExtraButton}
        {addExtraModal}
      </div>
    );
  }

  return (
    <div style={sectionStyle}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 12,
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={iconWrapperStyle}>
            <ToolOutlined />
          </div>
          <Text style={{ fontSize: 15, fontWeight: 700, color: '#1a3353', letterSpacing: 0.2 }}>
            Services
          </Text>
          <Badge
            count={serviceOrders.length}
            style={{
              backgroundColor: '#2CBDFF',
              fontSize: 11,
              fontWeight: 600,
              boxShadow: '0 1px 4px rgba(44, 189, 255, 0.3)',
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {addExtraButton}
          <div
            style={{
              display: 'flex',
              gap: 16,
              fontSize: 14,
              alignItems: 'center',
              background: '#fff',
              border: '1px solid #d4e4f7',
              borderRadius: 8,
              padding: '8px 16px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}
          >
            <span style={{ color: '#555' }}>
              Labour:{' '}
              <strong style={{ color: '#1a3353' }}>{Utils.formatCurrency(grandLabour)}</strong>
            </span>
            <span style={{ color: '#ccc' }}>|</span>
            <span style={{ color: '#555' }}>
              Parts:{' '}
              <strong style={{ color: '#1a3353' }}>{Utils.formatCurrency(grandParts)}</strong>
            </span>
            <span style={{ color: '#ccc' }}>|</span>
            <span style={{ color: '#555' }}>
              VAT: <strong style={{ color: '#1a3353' }}>{Utils.formatCurrency(grandVat)}</strong>
            </span>
            <span style={{ color: '#ccc' }}>|</span>
            <Tag
              color="blue"
              style={{ fontSize: 15, fontWeight: 700, padding: '2px 12px', margin: 0 }}
            >
              {Utils.formatCurrency(grandTotal)}
            </Tag>
          </div>
        </div>
      </div>

      {serviceOrders.map(renderServiceCard)}
      {addExtraModal}
    </div>
  );
});
